import React from 'react';
import styled, {css} from 'styled-components';
import {colorTheme} from '../../../mixin/colorTheme';
import {Icon} from '../../Icon/Icon';
import {btnSize} from '../btn-conditions/btnSizeCondition';
import {btnColorCondition, btnType} from '../btn-conditions/btnTypeCondition';
import {basicBtnSetup} from '../btn-setup/btnStyling';

/**
 * @typedef {object} BtnProps
 *
 * @property {boolean} [round]
 * If true, the button will have rounded corners.
 *
 * @property {boolean} [ghost]
 * If true, the button will have a ghost style with a transparent background.
 *
 * @property {boolean} [link]
 * If true, the button will have a link style with a transparent background and a transparent border.
 *
 * @property {boolean} [subtle]
 * If true, the button will have a gray border, white background, and also colored text and icon based on the color you pick.
 * This button is meant as a secondary type design. The purpose here is to make the button subtle for the user to concentrate on whatever page content he is looking at.
 *
 * @property {string} [color]
 * CSS variable names to use for:
 *   - 'background color if default'
 *   - 'color for link type (text only)'
 *   - 'text color and outline color for ghost type'
 *
 * The color of the button, which can be one of the following options:
 *   - 'primary'
 *   - 'success'
 *   - 'danger'
 *   - 'default'
 *
 * @property {string} [size]
 * CSS variable names to use for a different button size:
 *
 * The size of the button, which can be one of the following options:
 *   - 'sm'
 *   - 'lg'
 */

/**
 * @description Button & anchor component setup for Tarasa.
 * See buttons.md for more information on when and how to use.
 *
 * @type {import('styled-components').StyledComponent<"button", any, BtnProps, never>}
 */
export const Btn = styled.button`
    ${basicBtnSetup};
    ${({color, round, ghost, link, subtle}) => btnType(color, round, ghost, link, subtle)};
    ${({size}) => btnSize(size)};

    ${colorTheme({
        // === black ===
        '--btn-default     ': {dark: 'var(--honey-00)  ', light: 'var(--gray-13)'},
        '--btn-default-dark': {dark: 'var(--honey-01)  ', light: 'var(--gray-14)'},
    })};
    ${({color, ghost, link}) => btnColorCondition(color, ghost, link)};

    ${({children, link, subtle, color}) => {
        const isWithIcon = React.Children.toArray(children).some((child) => child.type === Icon);
        const isOnlyIcon = React.Children.count(children) === 1 && isWithIcon;

        if (isOnlyIcon && link && subtle) {
            return css`
                color: var(--label-text-color);
                &:hover {
                    color: var(--${color});
                }
                &:disabled,
                &[disabled] {
                    color: var(--disabled) !important;
                }
            `;
        }
    }}
`;
